import React from 'react';
import styled, { css } from 'styled-components';
import { selectColor, sendTrackingInfo } from '@panel/commons';
import { getColor } from '@panel/commons/core/themev3';
import CalendarIcon from '@panel/commons/assets/icons/calendar.svg';
import { useTranslation } from 'react-i18next';
import { getDevice } from '@panel/commons/helpers';
import TooltipHelp from '@panel/commons/assets/icons/tooltip-helper.svg';
import SimpleTooltip from '@panel/commons/lib/Tooltip/SimpleTooltip';
import { useDispatch, useSelector } from 'react-redux';
import { performanceSelectors, performanceOperations } from '../../ducks/performance';
import ComparisonTable from './ComparisonTable';

function Table({
  posting,
  postingId,
  tableData,
  comparisonType,
  setComparisonType,
  hasBestData,
  isSegmentDataAvailable
}) {
  const { t } = useTranslation();
  const device = getDevice();
  const dispatch = useDispatch();
  const postingStatistics = useSelector(performanceSelectors.getPerformanceStatistics);
  const { priceError, priceLoading } = postingStatistics;
  const segmentDataAvailable =
    postingStatistics.postingStatistics && postingStatistics.postingStatistics['segment-leads'] >= 1;
  const isMobile = device === 'mobile';

  const onReload = () => dispatch(performanceOperations.getPriceStatistics(postingId));

  const tooltipText = `${t('panel.desempeno.table.info')} ${posting.realestate_type} ${t(
    'panel.desempeno.table.info.en'
  )} ${posting.operationType} ${t('panel.desempeno.table.info.en')} ${posting.zone}`;

  const tooltipTextComponent = <TooltipComponent>{tooltipText}</TooltipComponent>;

  return (
    <>
      {tableData && (segmentDataAvailable || isSegmentDataAvailable) && (
        <ContentLayout isMobile={isMobile}>
          <SectionHeader isMobile={isMobile} device={device}>
            <div>
              <div className='title' data-panel='container-performance-table'>
                <div>
                  <h2 data-panel='performance-table-title'>{t('panel.desempeno.table.title')}</h2>
                  {isMobile && (
                    <SimpleTooltip
                      offset={5}
                      backgroundColor={selectColor('black')}
                      isMobile={false}
                      text={tooltipTextComponent}
                      position='bottom-center'
                      dataPanel='title-table-info'
                    >
                      <img src={TooltipHelp} alt='' width='18' height='18' />
                    </SimpleTooltip>
                  )}
                </div>
                <div className='subtitle' data-panel='performance-table-subTitle'>
                  <img src={CalendarIcon} alt='Calendario' />
                  {`${t('panel.desempeno.table.subtitle')} `}
                  {t('panel.desempeno.table.subtitle.strong')}
                </div>
              </div>
              {posting && !isMobile && (
                <p data-panel='performance-table-info'>
                  {t('panel.desempeno.table.info')}{' '}
                  <span data-panel='performance-table-info-type'>{posting.realestate_type} </span>
                  {posting.rooms && JSON.parse(posting.rooms) > 0 && (
                    <>
                      <span data-panel='performance-table-info-number-rooms'>{posting.rooms} </span>
                      <span data-panel='performance-table-info-text-rooms'>
                        {t(`panel.desempeno.comparativo.segmento.rooms${posting.rooms > 1 ? '.plural' : ''}`)}{' '}
                      </span>
                    </>
                  )}
                  {posting.operationType && `${t('panel.desempeno.table.info.en')} `}
                  <span data-panel='performance-table-operationType'>{posting.operationType} </span>
                  {t('panel.desempeno.table.info.en')}{' '}
                  <span data-panel='performance-table-info-zone'>{posting.zone}</span>
                </p>
              )}
            </div>
            {hasBestData && (
              <ToggleButton isMobile={isMobile}>
                <OptionArea
                  isActive={comparisonType === 'average'}
                  onClick={() => {
                    sendTrackingInfo('Panel', 'Mis avisos', 'Desempeño del aviso-Comparativa-Aviso promedio');
                    setComparisonType('average');
                  }}
                  data-panel='desempeno-table-button-avgPosting'
                >
                  {t('panel.desempeno.table.average')}
                </OptionArea>
                <OptionArea
                  isActive={comparisonType === 'better'}
                  onClick={() => {
                    sendTrackingInfo('Panel', 'Mis avisos', 'Desempeño del aviso-Comparativa-Mejor aviso');
                    setComparisonType('better');
                  }}
                  data-panel='desempeno-table-button-bestPosting'
                >
                  {t('panel.desempeno.table.better')}
                </OptionArea>
              </ToggleButton>
            )}
          </SectionHeader>
          <ComparisonTable
            headers={tableData.headers}
            rows={tableData.rows}
            priceLoading={priceLoading}
            priceError={priceError}
            onReload={onReload}
            isMobile={isMobile}
            comparisonType={comparisonType}
          />
        </ContentLayout>
      )}
    </>
  );
}

const ToggleButton = styled.div`
  display: flex;
  height: 48px;
  width: 269px;
  border: 1px solid ${selectColor('grayDark')};
  border-radius: 7px;
  overflow: hidden;
  ${({ isMobile }) =>
    isMobile &&
    css`
      width: 100%;
      margin-bottom: 18px;
    `}
`;

const OptionArea = styled.div`
  height: 100%;
  flex: 1;
  color: ${selectColor('grayDark')};
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 14px;
  cursor: pointer;
  ${({ isActive }) =>
    isActive &&
    css`
      font-weight: 600;
      background-color: ${getColor('primary')};
      color: white;
    `}
`;

const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: ${({ device }) => {
    if (device === 'desktop') return '22px';
    if (device === 'tablet') return '20px';
    return '0';
  }};
  ${({ isMobile }) =>
    isMobile &&
    css`
      flex-direction: column;
    `}
`;

const ContentLayout = styled.div`
  width: 100%;
  margin-top: ${({ isMobile }) => (isMobile ? '24px' : '32px')};
  margin-bottom: 44px;
  .title {
    display: flex;
    align-items: center;
    color: ${selectColor('grayDark')};
    font-weight: 500;
    font-size: 12px;
    margin-bottom: ${({ isMobile }) => (isMobile ? '29px' : '0')};
    div {
      display: flex;
      align-items: center;
      h2 {
        font-weight: 700;
        color: black;
        margin-right: 12px;
        font-size: 24px;
        img {
          width: 16px;
          height: 16px;
          margin-left: 6px;
        }
      }
    }
    ${({ isMobile }) =>
      isMobile &&
      css`
        flex-direction: column;
        align-items: flex-start;
      `}
  }
  .subtitle {
    display: flex;
    align-items: center;
    > img {
      width: 16px;
      height: 16px;
      margin: 0px 5px 3px 0px;
    }
  }
  p {
    color: ${selectColor('grayDark')};
    white-space: normal;
    font-size: 14px;
    span {
      font-weight: 500;
      color: black;
    }
  }
`;

const TooltipComponent = styled.div`
  max-width: 200px;
  white-space: pre-wrap;
  text-align: center;
  font-size: 14px;
`;

export default Table;
